/* eslint-disable no-restricted-globals */
import { useEffect, useRef, useState } from 'react';

import { THEME } from 'src/enums/theme';

type RouteThemeMap = {
  [key: string]: {
    value: THEME;
    // Optional weblab to be evaluated by the calling component before applying theme.
    weblab?: string;
  };
};

export const DEFAULT_THEME = { value: THEME.LIGHT };

// Define the default route colors
const THEME_BY_ROUTE: RouteThemeMap = {
  '/': DEFAULT_THEME,
  '/credits': { value: THEME.DARK },
  '/credits/storefront': { value: THEME.REBRAND_DARK },

  // ASX Credits Frontend Routes
  // Credits section
  '/credits/interest': { value: THEME.REBRAND_LIGHT },
  '/credits/interest/*': { value: THEME.REBRAND_LIGHT },
  '/credits/product': { value: THEME.REBRAND_LIGHT },
  '/credits/product/*': { value: THEME.REBRAND_LIGHT },

  // Account section
  '/account': { value: THEME.REBRAND_LIGHT },
  '/account/*': { value: THEME.REBRAND_LIGHT },
};

/**
 * Find matching theme config for a given path, supporting wildcards.
 * Uses an optimized algorithm that prioritizes exact matches first,
 * then sorts wildcard patterns by specificity (longest first) to ensure
 * the most specific route pattern is selected.
 */
const findThemeConfig = (path: string): { value: THEME; weblab?: string } => {
  // First try exact match
  if (THEME_BY_ROUTE[path]) return THEME_BY_ROUTE[path];

  // For wildcard matches, sort by specificity (length) first
  const wildcardPatterns = Object.keys(THEME_BY_ROUTE)
    .filter((pattern) => pattern.endsWith('/*'))
    .sort((a, b) => b.length - a.length); // Longer patterns first

  const matchingPattern = wildcardPatterns.find((pattern) => {
    const baseRoute = pattern.slice(0, -2);
    return path.startsWith(`${baseRoute}/`) || path === baseRoute;
  });

  if (matchingPattern) {
    return THEME_BY_ROUTE[matchingPattern];
  }

  return DEFAULT_THEME;
};

/**
 * Returns theme config data to be used for the current page route.
 * Weblabs should be evaluated by the calling component before applying theme.
 * @returns Theme config data
 */
export const useThemeConfig = () => {
  const [themeConfig, setThemeConfig] = useState<{ value: THEME; weblab?: string }>(DEFAULT_THEME);

  let previousUrl = '';

  // Utilize a MutationObserver against the root document to listen for url changes
  // https://tiny.amazon.com/1s8r9h47/url-listening
  // https://tiny.amazon.com/w7rjjvqw/mutation-observers
  const observer = useRef(
    new MutationObserver(() => {
      if (location.href !== previousUrl) {
        previousUrl = location.href;
        const path = location.pathname.replace(/\/+$/, '');
        setThemeConfig(findThemeConfig(path));
      }
    })
  );

  useEffect(() => {
    const observerConfig = { subtree: true, childList: true };
    observer.current.observe(document, observerConfig);
  }, [observer]);

  return { themeConfig, observer: observer.current };
};
